import {
  bindSiteAssetsInputSchema,
  unassignedSiteAssetsInputSchema,
} from "@crm/domain/site-inputs";
/**
 * Sites RPC 契约（两个挂载点，两个组）：
 *
 * - SitesRpc：apps/workbench sites（workbench_sites 主干，6 端点），挂 workbench server。
 * - CrmSitesRpc：CRM fulfillment/sites 域（delivery sites 表，7 端点），挂 CRM server。
 *   两个 server 的 AppRpc 不同，tag createSite/updateSite/deleteSite 不冲突。
 *
 * CrmSitesRpc 端点权限映射（实现层 requirePermissionsEffect 声明，与旧 serverFn
 * middleware access([...]) 一一对应）：
 * - getSites / getSitesWithStudents / getSitesList / getSitesPaginated →
 *   DELIVERY_READ_ALL | DELIVERY_READ_ASSIGNED（resolveScope byStudent）
 * - createSite / updateSite → DELIVERY_WRITE_ALL | DELIVERY_WRITE_ASSIGNED
 *   （verifyWriteScope；online 状态需 DELIVERY_WRITE_ALL）
 * - deleteSite → DELIVERY_WRITE_ALL
 *
 * 成功侧为精确 Schema：Date 字段 wire=ISO string，decode 回 Date（消费侧恢复 Date 语义）。
 */
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";
import { sitePlatformEnum, siteStatusEnum } from "@crm/db/schema/fulfillment";
import { ALL_CAPABILITY_IDS } from "@crm/domain/capabilities";
import {
  createSiteInputSchema,
  deleteSiteInputSchema,
  getSitesInputSchema,
  getSitesListInputSchema,
  getSitesPaginatedInputSchema,
  getSitesWithStudentsInputSchema,
  updateSiteInputSchema,
} from "@crm/domain/media-sites";
import {
  createSiteInputSchema as workbenchCreateSiteInputSchema,
  siteIdInputSchema,
  undefinedInputSchema,
  updateSiteInputSchema as workbenchUpdateSiteInputSchema,
} from "@crm/domain/site-inputs";
import { Schema } from "effect";
import { asContractSchema, RpcAppError } from "../errors";
import { RpcAuthMiddleware } from "../middleware";

const SiteStatusSchema = Schema.Literals([...siteStatusEnum.enumValues]);
const SitePlatformNullableSchema = Schema.NullOr(Schema.Literals([...sitePlatformEnum.enumValues]));

// =============================================================================
// SitesRpc（workbench_sites 主干，workbench server 挂载）
// =============================================================================

/** Workbench 站点行（workbench_sites 表；Date 字段 wire=ISO string，decode 回 Date） */
export const WorkbenchSiteSchema = Schema.Struct({
  id: Schema.String,
  userId: Schema.String,
  name: Schema.String,
  domain: Schema.NullOr(Schema.String),
  businessInfo: Schema.String,
  customerInfo: Schema.String,
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
  deletedAt: Schema.NullOr(Schema.DateFromString),
});
export type WorkbenchSiteWire = Schema.Schema.Type<typeof WorkbenchSiteSchema>;

/** getMySites 响应：站点列表 + 活动站点 ID（活动站点失效时回落到第一个站点） */
export const MySitesResultSchema = Schema.Struct({
  success: Schema.Literal(true),
  data: Schema.Struct({
    sites: Schema.mutable(Schema.Array(WorkbenchSiteSchema)),
    activeSiteId: Schema.NullOr(Schema.String),
  }),
});
export type MySitesResultWire = Schema.Schema.Type<typeof MySitesResultSchema>;

/** getMyCapabilities 响应：课程门禁推导的能力集 */
export const MyCapabilitiesResultSchema = Schema.Struct({
  success: Schema.Literal(true),
  data: Schema.mutable(Schema.Array(Schema.Literals(ALL_CAPABILITY_IDS))),
});
export type MyCapabilitiesResultWire = Schema.Schema.Type<typeof MyCapabilitiesResultSchema>;

/** createSite 响应：新建站点 ID */
export const WorkbenchSiteCreatedResultSchema = Schema.Struct({
  success: Schema.Literal(true),
  data: Schema.Struct({ id: Schema.String }),
});
export type WorkbenchSiteCreatedResultWire = Schema.Schema.Type<
  typeof WorkbenchSiteCreatedResultSchema
>;

/** updateSite / setActiveSite / deleteSite 响应 */
export const WorkbenchSiteOkResultSchema = Schema.Struct({ success: Schema.Literal(true) });
export type WorkbenchSiteOkResultWire = Schema.Schema.Type<typeof WorkbenchSiteOkResultSchema>;

export class SitesRpc extends RpcGroup.make(
  Rpc.make("getUnassignedSiteAssets", {
    payload: asContractSchema(unassignedSiteAssetsInputSchema),
    success: Schema.Array(Schema.Struct({ id: Schema.String, name: Schema.String })),
    error: RpcAppError,
  }),
  Rpc.make("bindSiteAssets", {
    payload: asContractSchema(bindSiteAssetsInputSchema),
    success: Schema.Struct({ boundCount: Schema.Finite }),
    error: RpcAppError,
  }),
  Rpc.make("getMySites", {
    payload: asContractSchema(undefinedInputSchema),
    success: MySitesResultSchema,
    error: RpcAppError,
  }),
  Rpc.make("createSite", {
    payload: asContractSchema(workbenchCreateSiteInputSchema),
    success: WorkbenchSiteCreatedResultSchema,
    error: RpcAppError,
  }),
  Rpc.make("updateSite", {
    payload: asContractSchema(workbenchUpdateSiteInputSchema),
    success: WorkbenchSiteOkResultSchema,
    error: RpcAppError,
  }),
  Rpc.make("setActiveSite", {
    payload: asContractSchema(siteIdInputSchema),
    success: WorkbenchSiteOkResultSchema,
    error: RpcAppError,
  }),
  Rpc.make("deleteSite", {
    payload: asContractSchema(siteIdInputSchema),
    success: WorkbenchSiteOkResultSchema,
    error: RpcAppError,
  }),
  Rpc.make("getMyCapabilities", {
    payload: asContractSchema(undefinedInputSchema),
    success: MyCapabilitiesResultSchema,
    error: RpcAppError,
  }),
).middleware(RpcAuthMiddleware) {}

// =============================================================================
// CrmSitesRpc（CRM fulfillment/sites 域，delivery sites 表，CRM server 挂载）
// =============================================================================

/** 站点行公共字段（delivery sites 表） */
const siteRowFields = {
  id: Schema.String,
  studentId: Schema.String,
  classSessionId: Schema.NullOr(Schema.String),
  url: Schema.NullOr(Schema.String),
  platform: SitePlatformNullableSchema,
  status: SiteStatusSchema,
  notes: Schema.NullOr(Schema.String),
  createdBy: Schema.NullOr(Schema.String),
  completedMonth: Schema.NullOr(Schema.String),
  completedBy: Schema.NullOr(Schema.String),
  completedAt: Schema.NullOr(Schema.DateFromString),
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
};

/** 站点行（delivery sites 表；Date 字段 wire=ISO string，decode 回 Date） */
export const SiteSchema = Schema.Struct(siteRowFields);
export type SiteWire = Schema.Schema.Type<typeof SiteSchema>;

/** 站点行 + 学员名（服务端 SQL JOIN，getSitesWithStudents） */
export const SiteWithStudentSchema = Schema.Struct({
  ...siteRowFields,
  studentName: Schema.NullOr(Schema.String),
});
export type SiteWithStudentWire = Schema.Schema.Type<typeof SiteWithStudentSchema>;

/** 建站课网站列表项（getSitesList，限定 wp 课程班期） */
export const SiteListItemSchema = Schema.Struct({
  id: Schema.String,
  studentId: Schema.String,
  url: Schema.NullOr(Schema.String),
  platform: SitePlatformNullableSchema,
  status: SiteStatusSchema,
  notes: Schema.NullOr(Schema.String),
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
  studentName: Schema.NullOr(Schema.String),
  enrollmentClassSessionId: Schema.NullOr(Schema.String),
});
export type SiteListItemWire = Schema.Schema.Type<typeof SiteListItemSchema>;

/** 分页列表项（getSitesPaginated 行） */
export const SitePaginatedItemSchema = Schema.Struct({
  id: Schema.String,
  studentId: Schema.String,
  classSessionId: Schema.NullOr(Schema.String),
  url: Schema.NullOr(Schema.String),
  platform: SitePlatformNullableSchema,
  status: SiteStatusSchema,
  notes: Schema.NullOr(Schema.String),
  completedMonth: Schema.NullOr(Schema.String),
  completedBy: Schema.NullOr(Schema.String),
  completedAt: Schema.NullOr(Schema.DateFromString),
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
  studentName: Schema.NullOr(Schema.String),
});
export type SitePaginatedItemWire = Schema.Schema.Type<typeof SitePaginatedItemSchema>;

/** 站点状态统计（getSitesPaginated.stats） */
export const SiteStatsSchema = Schema.Struct({
  total: Schema.Finite,
  online: Schema.Finite,
  building: Schema.Finite,
  stalled: Schema.Finite,
  not_started: Schema.Finite,
  pending_review: Schema.Finite,
  need_improve: Schema.Finite,
});
export type SiteStatsWire = Schema.Schema.Type<typeof SiteStatsSchema>;

/** 分页响应（getSitesPaginated） */
export const SitesPaginatedResponseSchema = Schema.Struct({
  items: Schema.mutable(Schema.Array(SitePaginatedItemSchema)),
  pagination: Schema.Struct({
    page: Schema.Finite,
    pageSize: Schema.Finite,
    total: Schema.Finite,
    totalPages: Schema.Finite,
  }),
  stats: SiteStatsSchema,
});
export type SitesPaginatedResponseWire = Schema.Schema.Type<typeof SitesPaginatedResponseSchema>;

/** deleteSite 响应 */
export const CrmSiteDeletedResultSchema = Schema.Struct({
  success: Schema.Literal(true),
  id: Schema.String,
});
export type CrmSiteDeletedResultWire = Schema.Schema.Type<typeof CrmSiteDeletedResultSchema>;

/** CRM fulfillment/sites 域（delivery sites 表，挂 CRM server） */
export class CrmSitesRpc extends RpcGroup.make(
  Rpc.make("getSites", {
    payload: asContractSchema(getSitesInputSchema),
    success: Schema.mutable(Schema.Array(SiteSchema)),
    error: RpcAppError,
  }),
  Rpc.make("getSitesWithStudents", {
    payload: asContractSchema(getSitesWithStudentsInputSchema),
    success: Schema.mutable(Schema.Array(SiteWithStudentSchema)),
    error: RpcAppError,
  }),
  Rpc.make("getSitesList", {
    payload: asContractSchema(getSitesListInputSchema),
    success: Schema.mutable(Schema.Array(SiteListItemSchema)),
    error: RpcAppError,
  }),
  Rpc.make("getSitesPaginated", {
    payload: asContractSchema(getSitesPaginatedInputSchema),
    success: SitesPaginatedResponseSchema,
    error: RpcAppError,
  }),
  Rpc.make("createSite", {
    payload: asContractSchema(createSiteInputSchema),
    success: SiteSchema,
    error: RpcAppError,
  }),
  Rpc.make("updateSite", {
    payload: asContractSchema(updateSiteInputSchema),
    success: SiteSchema,
    error: RpcAppError,
  }),
  Rpc.make("deleteSite", {
    payload: asContractSchema(deleteSiteInputSchema),
    success: CrmSiteDeletedResultSchema,
    error: RpcAppError,
  }),
).middleware(RpcAuthMiddleware) {}
